import Table from 'common/table/table';
import { ThemeHeader } from 'common/table/table-helper';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedNumber, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Col, Div, Dropdown, FormItem, FormText, Row, SubTitle } from 'ui';

const Wrapper = styled(Div)`
  box-shadow: rgba(0, 0, 0, 0.27) 0px;  
  border-radius: 4px;
  background-color: #fff;
  margin-bottom: 20px;
`;

// eslint-disable-next-line
const TaskList = ({
  data,
  visitTypes,
  onVisitTypeChange,
  intl,
}) => {
  const { formatMessage: translate } = intl;

  const operationsColumns = [
    {
      id: 'description',
      displayName: translate({ id: 'equipment.taskList.OPERATION' }),
      fieldName: 'description',
      tooltip: row => row.description,
    },
  ];

  const partsColumns = [
    {
      id: 'materialNumber',
      displayName: translate({ id: 'equipment.taskList.MATERIAL_NUMBER' }),
      fieldName: 'materialNumber',
    },
    {
      id: 'materialDescription',
      displayName: translate({ id: 'equipment.taskList.MATERIAL_DESC' }),
      fieldName: 'materialDescription',
      tooltip: row => row.materialDescription,
    },
    {
      id: 'quantity',
      displayName: translate({ id: 'equipment.taskList.QUANTITY' }),
      renderFn: value => `${value?.quantity} ${value?.quantityUnit}`,
    },
    {
      id: 'costPrice',
      displayName: translate({ id: 'equipment.taskList.PRICE' }),
      renderFn: value => value?.costCurrency ? <FormattedNumber
        value={value?.costPrice}
        // eslint-disable-next-line
        style='currency'
        currency={value?.costCurrency}
      /> : value?.costPrice ?? '',
    },
    {
      id: 'vendor',
      displayName: translate({ id: 'equipment.taskList.VENDOR' }),
      fieldName: 'vendor',
      tooltip: row => row.vendor,
    },
  ];

  const translateUnit = (uom) => {
    const translatableUoms = ['STD', 'KMT', 'MIL'];
    if (translatableUoms.includes(uom)) {
      return translate({ id: `equipment.taskList.uom.${uom}` });
    }
    return uom;
  };

  const renderHeader = (title, amount = 0) => <>
    <ThemeHeader inlineTable={true}>
      <span>
        {`${intl.formatMessage({ id: title })} `}
        ({amount})
      </span>
    </ThemeHeader>
  </>;

  if (!data || !Object.keys(data).length) {
    return null;
  }

  return (
    <Div marginBottom={20} data-test-id='task-list'>
      <SubTitle>{intl.formatMessage({ id: 'common.titles.TASKLIST' })}</SubTitle>
      <Row type='flex' gutter={16} marginRight={0}>
        <Col md={6} sm={24} xs={24} paddingRight={55}>
          <FormItem label={intl.formatMessage({ id: 'taskList.TYPES' })} colon={false}>
            <Dropdown
              values={visitTypes}
              valueKey='id'
              valueLabel='text'
              value={data?.id}
              onChange={selectedType => onVisitTypeChange(selectedType)}
            />
          </FormItem>
        </Col>
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.LABOR_TIME' })} colon={false}>
            <FormText>
              {data?.laborTime ?
                <span>
                  <FormattedNumber value={data?.laborTime?.value} />
                  {translateUnit(data?.laborTime?.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.TRAVEL_TIME' })} colon={false}>
            <FormText>
              {data?.travelTime ?
                <span>
                  <FormattedNumber value={data?.travelTime?.value} />{' '}
                  {translateUnit(data?.travelTime?.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.TRAVEL_DISTANCE' })} colon={false}>
            <FormText>
              {data?.travelDistance ?
                <span>
                  <FormattedNumber value={data?.travelDistance?.value} />{' '}
                  {translateUnit(data?.travelDistance?.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.EXPENSES' })} colon={false}>
            <FormText>
              {data?.expenses ?
                <span>
                  <FormattedNumber value={data?.expenses?.value} />
                  {translateUnit(data?.expenses?.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16} marginRight={0}>
        <Col md={6} sm={24} xs={24} /> {/* to add blank column */}
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.ALLOWANCE' })} colon={false}>
            <FormText>
              {data?.allowance ?
                <span>
                  <FormattedNumber value={data?.allowance?.value} />
                  {translateUnit(data?.allowance.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.ENVIRONMENTAL' })} colon={false}>
            <FormText>
              {data?.environmental ?
                <span>
                  <FormattedNumber value={data?.environmental?.value} />{' '}
                  {translateUnit(data?.environmental?.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.CHEM_ANALYSIS' })} colon={false}>
            <FormText>
              {data?.chemAnalysis ?
                <span>
                  <FormattedNumber value={data?.chemAnalysis?.value} />{' '}
                  {translateUnit(data?.chemAnalysis?.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={3} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'taskList.REFRIGRANT' })} colon={false}>
            <FormText>
              {data?.refrigrant ?
                <span>
                  <FormattedNumber value={data?.refrigrant.value} />{' '}
                  {translateUnit(data?.refrigrant.unit)}
                </span>
                :
                <br />
              }
            </FormText>
          </FormItem>
        </Col>
      </Row>
      <Wrapper marginBottom={10} data-test-id='tasklist.operations'>
        <Table
          renderCustomHeader={() => renderHeader(
            'equipment.taskList.OPERATIONS',
            data?.operations.length,
            operationsColumns,
            data?.operations,
          )}
          hasCustomHeader={true}
          data={data?.operations || []}
          columns={operationsColumns}
          fullWidth={true}
          lightHeader={true}
        />
      </Wrapper>
      <Wrapper data-test-id='tasklist.parts'>
        <Table
          renderCustomHeader={() => renderHeader(
            'equipment.taskList.PARTS',
            data?.parts.length,
            partsColumns,
            data?.parts,
          )}
          hasCustomHeader={true}
          data={data?.parts || []}
          columns={partsColumns}
          fullWidth={true}
          lightHeader={true}
        />
      </Wrapper>
    </Div>
  );
};

TaskList.propTypes = {
  data: PropTypes.object,
  visitTypes: PropTypes.array,
  onVisitTypeChange: PropTypes.func,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(TaskList);
